import { ReactNode, useEffect, useState } from 'react';
import Container from '@v2/shared/components/Container';
import ProductBand from '@v2/shared/components/ProductBand';
import Spinner from '@v2/shared/components/Spinner';
import { Category, Product } from '@v2/shared/lib/types';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';
import { wafFetch } from '@v2/shared/lib/wafFetch';

/**
 * Enough to fill the widest desktop and still have somewhere to scroll.
 *
 * A card and its gap come to 154px, and the container tops out at 1536px with 20px of
 * padding each side, so nine fit on the largest screen. Twelve covers that with slack.
 * The endpoint caps `limit` at 20, so there is room above this if the layout changes.
 */
const PER_SECTION = 12;

/**
 * The bands are the children of this category. Their titles and ordering come from the
 * category list, so bands can be added or reordered without touching this code.
 */
const PARENT_CATEGORY = 'bulk-order-templates';

type Props = {
  heading: string;
  subtitle?: ReactNode;
  /**
   * Which band to show. Absent on the hub, which shows every child of the parent category;
   * a variant passes the single child slug it maps to, so only that one band renders.
   */
  categorySlug?: string;
};

type LoadedBand = { title: string; products: Product[] };

/**
 * Fetched on the client rather than passed as props.
 *
 * The children of the parent category drive the bands — one request lists them, then each
 * band's products load in parallel, since they do not depend on each other. A category that
 * comes back with no products is dropped rather than shown as an empty strip.
 *
 * This runs exactly once. `buildUrl` is a fresh function on every render, so depending on it
 * would re-run the effect after each `setState` and hammer the endpoint in a loop; the site
 * base url behind it is stable, so it is bound once here and never retried — success or not.
 *
 * Print only. A bulk order is physical copies for a waiting room or a library, so a digital
 * title here is one nobody can order the way this page sells.
 *
 * Non-visible titles included. These bands are hand-picked in the admin, and a title being
 * held back from the channel feeds is not a reason to drop it from a list someone curated
 * on purpose — without this the strips silently lose members of their own category.
 */
export default function PopularTitles({ heading, subtitle, categorySlug }: Props) {
  const { buildUrl } = useUrlGenerator();
  const [sections, setSections] = useState<LoadedBand[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const loadProducts = (slug: string): Promise<Product[]> =>
      wafFetch(
        buildUrl(
          `/services_api/storefront/v1/products.json?category=${encodeURIComponent(slug)}` +
            `&sort_by=mpopularity&format=print&include_non_visible=1&limit=${PER_SECTION}`
        )
      )
        .then((response) => (response.ok ? response.json() : null))
        .then((data) =>
          // A title with no cover would leave a hole in a strip whose content is covers.
          ((data?.results ?? []) as Product[]).filter((product) => product.featured_image?.url)
        )
        .catch(() => []);

    const listCategories = (): Promise<Category[]> =>
      wafFetch(
        buildUrl(
          `/services_api/storefront/v1/categories.json?parent=${encodeURIComponent(PARENT_CATEGORY)}`
        )
      )
        .then((response) => (response.ok ? response.json() : null))
        .then((data) => (data?.results ?? []) as Category[])
        .catch(() => []);

    listCategories()
      .then((categories) => {
        const selected = categorySlug
          ? categories.filter((category) => category.slug === categorySlug)
          : categories;

        return Promise.all(
          selected.map((category) =>
            loadProducts(category.slug ?? '').then((products) => ({
              title: category.name,
              products,
            }))
          )
        );
      })
      .then((loaded) => {
        if (cancelled) {
          return;
        }

        // Skip any category with no products — the strip is covers or nothing.
        setSections(loaded.filter((section) => section.products.length > 0));
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // Bound once on mount — see the note above on why buildUrl is not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Nothing to show and nothing pending: keep the page clean rather than leaving a bare
  // heading over empty space (the curated sections can be temporarily empty).
  if (!isLoading && sections.length === 0) {
    return null;
  }

  /**
   * A sub-type page shows exactly one band, so the section heading and the band heading
   * sat one above the other saying the same thing. Here the section heading is the
   * category's own name — the admin's wording rather than a copy of it hardcoded in this
   * repo, so renaming the category renames the page — and the band itself renders
   * untitled. The hub is unchanged: it stacks several bands, so each still needs its own
   * heading to say which is which.
   *
   * The configured heading stays as the fallback, covering the moment before the category
   * list arrives and the case where it never does.
   */
  const isSingleBand = Boolean(categorySlug);
  const sectionHeading = (isSingleBand && sections[0]?.title) || heading;

  return (
    <section className="py-10 md:py-20 bg-slate-50">
      <Container>
        <h2 className="font-bold text-3xl lg:text-4xl text-center mb-3">{sectionHeading}</h2>

        {subtitle && (
          <p className="text-center text-slate-600 text-lg max-w-3xl mx-auto mb-8">{subtitle}</p>
        )}

        {isLoading ? (
          <div className="text-center">
            <Spinner />
          </div>
        ) : (
          sections.map((section) => (
            <ProductBand
              key={section.title}
              title={isSingleBand ? undefined : section.title}
              products={section.products}
              showDetails={false}
              // The conversion on this page is the quote form, so a visitor opening a
              // title to look at it should come back to a form they never left.
              openInNewTab
            />
          ))
        )}
      </Container>
    </section>
  );
}
